import jsPDF from "jspdf";

type WelcomeGuidePdfInput = {
  storeName: string;
  recipientName: string;
  locationName: string;
  scannerUrl: string;
  qrCodeDataUrl: string;
  logoDataUrl: string;
};

const safeFilenamePart = (value: string) =>
  value.trim().replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "").toLowerCase() || "store";

export const createWelcomeGuidePdf = ({
  storeName,
  recipientName,
  locationName,
  scannerUrl,
  qrCodeDataUrl,
  logoDataUrl,
}: WelcomeGuidePdfInput) => {
  const doc = new jsPDF({ unit: "pt", format: "letter", orientation: "portrait" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 44;
  const contentWidth = pageWidth - margin * 2;

  const setText = (size: number, bold = false, color: [number, number, number] = [31, 42, 68]) => {
    doc.setFont("helvetica", bold ? "bold" : "normal");
    doc.setFontSize(size);
    doc.setTextColor(...color);
  };
  const drawWrapped = (text: string, x: number, y: number, width: number, size = 9.5, bold = false, color: [number, number, number] = [51, 65, 85]) => {
    setText(size, bold, color);
    const lines = doc.splitTextToSize(text, width);
    doc.text(lines, x, y);
    return { lines, height: lines.length * size * 1.3 };
  };

  doc.addImage(logoDataUrl, "PNG", margin, 22, 34, 34);
  setText(21, true, [31, 86, 139]);
  doc.text("LottoReco", margin + 43, 47);
  doc.setDrawColor(220, 229, 237);
  doc.line(margin, 68, pageWidth - margin, 68);

  setText(22, true);
  doc.text("Welcome to LottoReco!", margin, 101);
  setText(10.5, true);
  doc.text(`Hi ${recipientName},`, margin, 125);
  drawWrapped(
    "Your store is ready. You can start using LottoReco today to audit your scratcher inventory without writing every ticket number in a spreadsheet.",
    margin,
    146,
    contentWidth,
    10
  );

  doc.setFillColor(241, 252, 246);
  doc.setDrawColor(172, 229, 197);
  doc.roundedRect(margin, 177, contentWidth, 40, 7, 7, "FD");
  setText(9.5, false, [31, 65, 52]);
  doc.text("At any point,", margin + 14, 201);
  setText(9.5, true, [20, 135, 74]);
  doc.text("contact us on WhatsApp at 650-888-3316", margin + 79, 201);
  setText(9.5, false, [31, 65, 52]);
  doc.text("and we will help you get started.", margin + 295, 201);
  doc.link(margin, 177, contentWidth, 40, { url: "https://wa.me/16508883316" });

  doc.setFillColor(247, 250, 252);
  doc.setDrawColor(218, 228, 237);
  doc.roundedRect(margin, 232, contentWidth, 218, 9, 9, "FD");
  setText(15, true);
  doc.text("Start Your First Scratcher Audit", pageWidth / 2, 258, { align: "center" });
  setText(10, true, [100, 116, 139]);
  doc.text(storeName, pageWidth / 2, 276, { align: "center" });
  setText(8, false, [100, 116, 139]);
  doc.text(`Scanning location: ${locationName}`, pageWidth / 2, 289, { align: "center" });

  const steps = [
    "Click the button below, or scan the QR code, to open the LottoReco Scanner.",
    "Enter cashier PIN 123456.",
    "Scan the scratcher packs currently in your display.",
  ];
  let stepY = 313;
  steps.forEach((step, index) => {
    doc.setFillColor(231, 248, 239);
    doc.circle(margin + 20, stepY - 3, 10, "F");
    setText(8.5, true, [8, 122, 76]);
    doc.text(String(index + 1), margin + 20, stepY, { align: "center" });
    const wrappedStep = drawWrapped(step, margin + 39, stepY, 280, 9, index === 1);
    stepY += Math.max(25, wrappedStep.height + 7);
  });

  doc.setFillColor(37, 110, 166);
  doc.roundedRect(margin + 39, 393, 275, 33, 5, 5, "F");
  setText(10, true, [255, 255, 255]);
  doc.text("Click Here to Start Scanning Inventory", margin + 176.5, 414, { align: "center" });
  doc.link(margin + 39, 393, 275, 33, { url: scannerUrl });

  const qrSize = 112;
  const qrX = pageWidth - margin - qrSize - 20;
  doc.addImage(qrCodeDataUrl, "PNG", qrX, 301, qrSize, qrSize);
  setText(8.5, true);
  doc.text("QR code to open the LottoReco Scanner", qrX + qrSize / 2, 425, { align: "center" });
  setText(7.8, false, [71, 85, 105]);
  doc.text("Place this guide next to your scratcher", qrX + qrSize / 2, 438, { align: "center" });
  doc.text("display case for quick cashier access.", qrX + qrSize / 2, 447, { align: "center" });

  doc.setFillColor(248, 250, 252);
  doc.setDrawColor(226, 232, 240);
  doc.roundedRect(margin, 462, contentWidth, 42, 6, 6, "FD");
  setText(8.7, false, [51, 65, 85]);
  doc.text("Using another device? Scan this QR code with its camera.", pageWidth / 2, 478, { align: "center" });
  doc.text("To manage users or change the cashier PIN, log in to lottoreco.com and go to Store -> Manage Users.", pageWidth / 2, 493, { align: "center" });
  doc.link(margin, 480, contentWidth, 18, { url: "https://lottoreco.com" });

  drawWrapped(
    "That is it. LottoReco will record your inventory and create a digital history of each audit. You can then use the LottoReco mobile app or website to see total inventory sales by shift or by day.",
    margin,
    527,
    contentWidth,
    9.5
  );

  setText(14, true);
  doc.text("Next Steps", margin, 572);
  setText(9.3, false, [51, 65, 85]);
  doc.text("LottoReco provides end-to-end reconciliation by verifying that:", margin, 590);

  [
    "Every activated and returned scratcher pack is accounted for.",
    "POS scratcher sales match the inventory removed.",
    "Lottery and scratcher payouts match the payouts recorded in your POS.",
    "Lotto sales reported by your POS match the lottery portal.",
  ].forEach((item, index) => {
    const bulletY = 612 + index * 18;
    doc.setFillColor(20, 154, 89);
    doc.circle(margin + 4, bulletY - 3, 2.5, "F");
    setText(8.8, false, [51, 65, 85]);
    doc.text(item, margin + 14, bulletY);
  });

  doc.setFillColor(241, 247, 253);
  doc.setDrawColor(204, 221, 236);
  doc.roundedRect(margin, 676, contentWidth, 54, 7, 7, "FD");
  setText(9.5, true);
  doc.text("When you are ready, we will help connect your:", margin + 14, 696);
  setText(9.2, true, [31, 86, 139]);
  doc.text("POS system", margin + 14, 716);
  doc.text("State lottery account", margin + 114, 716);

  setText(8.5, false, [100, 116, 139]);
  doc.text("The LottoReco Team", margin, 758);
  setText(10, true, [31, 86, 139]);
  doc.text("Account for every ticket.", pageWidth - margin, 758, { align: "right" });

  doc.save(`${safeFilenamePart(storeName)}-${safeFilenamePart(locationName)}-welcome-guide.pdf`);
};
